import { Download, ExternalLink, FileWarning, Loader2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useSignedAssetUrl } from "@/hooks/use-signed-asset-url";
import {
  displayFileName,
  inferFileKind,
  isInlineViewable,
  type FileKind,
} from "@/lib/file-type";

// Shared inline file viewer for content assets, used by both the partner
// Resources drawer and the admin asset-detail preview. Renders PDF / image /
// video / audio inline from a `log=0` signed URL (so a passive preview never
// counts as a view); office + unknown types fall back to a download CTA.
//
// Download mechanism: appending `&download=<name>` to the Supabase signed URL
// makes Storage serve Content-Disposition: attachment with the real filename.
// (The <a download> attribute is ignored cross-origin, so the query param is
// what actually forces the download — see file-type.ts displayFileName.)

const KIND_LABEL: Record<FileKind, string> = {
  pdf: "PDF",
  image: "Image",
  video: "Video",
  audio: "Audio",
  office: "Document",
  other: "File",
};

function downloadHref(url: string, name: string): string {
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}download=${encodeURIComponent(name)}`;
}

export function FileViewerBody({
  assetId,
  fileUrl,
  title,
  className,
}: {
  assetId: string;
  fileUrl: string;
  title: string;
  className?: string;
}) {
  const kind = inferFileKind(fileUrl);
  const name = displayFileName(fileUrl);
  const query = useSignedAssetUrl(assetId, { enabled: true, log: false });
  const url = query.data?.url;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="relative flex flex-1 items-center justify-center overflow-auto bg-muted/20">
        {query.isPending ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        ) : query.isError || !url ? (
          <PreviewFallback
            name={name}
            reason="Couldn't load this file. Try downloading it instead."
            url={url}
          />
        ) : !isInlineViewable(kind) ? (
          <PreviewFallback
            name={name}
            reason="This file type can't be previewed in the browser."
            url={url}
          />
        ) : kind === "pdf" ? (
          <iframe src={url} title={title} className="h-full w-full border-0" />
        ) : kind === "image" ? (
          <img
            src={url}
            alt={title}
            className="max-h-full max-w-full object-contain p-4"
          />
        ) : kind === "video" ? (
          <video src={url} controls className="max-h-full w-full" />
        ) : (
          // audio
          <audio src={url} controls className="w-[min(92%,32rem)]" />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t bg-background px-5 py-3 text-xs">
        <span className="truncate text-muted-foreground" title={name}>
          {name}
        </span>
        {url && (
          <div className="flex flex-shrink-0 items-center gap-2">
            <a
              href={downloadHref(url, name)}
              download={name}
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md bg-accent px-2.5 py-1 font-medium text-accent-foreground hover:opacity-90"
            >
              <Download className="h-3 w-3" />
              Download
            </a>
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
            >
              Open in new tab
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewFallback({
  name,
  reason,
  url,
}: {
  name: string;
  reason: string;
  url: string | undefined;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <FileWarning className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="max-w-xs text-sm text-muted-foreground">{reason}</p>
      {url && (
        <a
          href={downloadHref(url, name)}
          download={name}
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-accent-foreground hover:opacity-90"
        >
          <Download className="h-3.5 w-3.5" />
          Download {name}
        </a>
      )}
    </div>
  );
}

export function FileViewerDrawer({
  open,
  onOpenChange,
  assetId,
  fileUrl,
  title,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  assetId: string;
  fileUrl: string;
  title: string;
}) {
  const kind = inferFileKind(fileUrl);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl lg:max-w-4xl"
      >
        <SheetHeader className="border-b px-5 py-3">
          <SheetTitle className="font-display tracking-tight">{title}</SheetTitle>
          <SheetDescription className="text-[11px] uppercase tracking-wider">
            {KIND_LABEL[kind]}
          </SheetDescription>
        </SheetHeader>
        {/* Only mount the body (and fire the signed-URL fetch) while open. */}
        {open && (
          <FileViewerBody
            assetId={assetId}
            fileUrl={fileUrl}
            title={title}
            className="min-h-0 flex-1"
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
